import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Die vier Kohorten-Zahlen der Dashboard-Kacheln.
 *
 * Bisher lud das Dashboard dafuer den kompletten Kundenstamm und zaehlte im
 * Browser -- bei M&M Hairdesign 7.228 Datensaetze in acht aufeinanderfolgenden
 * Anfragen, fuer vier Zahlen. Die Datenbank liefert sie jetzt in einer
 * Anfrage.
 */
export interface CustomerCohortCounts {
  thisWeekCustomers: number;
  lastWeekCustomers: number;
  thisWeekLeads: number;
  lastWeekLeads: number;
}

export const LEERE_KOHORTEN: CustomerCohortCounts = {
  thisWeekCustomers: 0,
  lastWeekCustomers: 0,
  thisWeekLeads: 0,
  lastWeekLeads: 0,
};

/** Rohzeile der RPC in die Form bringen, die die Ansichten erwarten. */
export function mapCohortRow(row: unknown): CustomerCohortCounts {
  // Die RPC liefert eine Tabelle mit genau einer Zeile; supabase-js gibt sie
  // je nach Aufruf als Array oder als Objekt zurueck.
  const r = (Array.isArray(row) ? row[0] : row) as Record<string, unknown> | null | undefined;
  if (!r) return LEERE_KOHORTEN;

  const zahl = (v: unknown) => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
  };

  return {
    thisWeekCustomers: zahl(r.this_week_customers),
    lastWeekCustomers: zahl(r.last_week_customers),
    thisWeekLeads: zahl(r.this_week_leads),
    lastWeekLeads: zahl(r.last_week_leads),
  };
}

export async function fetchCustomerCohortCounts(
  tenantId: string | null,
  client: Pick<typeof supabase, 'rpc'> = supabase
): Promise<CustomerCohortCounts> {
  if (!tenantId) return LEERE_KOHORTEN;

  const { data, error } = await client.rpc('customer_cohort_counts' as never, {
    p_tenant: tenantId,
  } as never);

  if (error) throw error;
  return mapCohortRow(data);
}

export function useCustomerCohortCounts(tenantId: string | null) {
  const { data = LEERE_KOHORTEN, isLoading } = useQuery({
    queryKey: ['customer-cohort-counts', tenantId],
    queryFn: () => fetchCustomerCohortCounts(tenantId),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  return { ...data, isLoading };
}
